// src/services/syncManager.js - VERSION CORRIGÉE

import inspectionStorage from './inspectionStorage';
import apiService from './apiService';
import cloneManager from './cloneManager';
import syncService, { prepareStepForSync, attachPendingPhotosToSyncPayload } from './syncService';
import { saveRapportMetaFromApiRapport } from '../utils/rapportDisplay';

class SyncManager {
  constructor() {
    this.syncQueue = [];
    this.isSyncing = false;
    this.syncInterval = null;
  }

  // 🔥 Synchroniser une étape vers Base44
  async syncStep(missionId, stepKey, stepData) {
    if (!navigator.onLine) {
      console.log('🔴 Hors ligne - sync différée pour', stepKey);
      this.addToSyncQueue(missionId, stepKey, stepData);
      return false;
    }

    try {
      console.log('🔍 ========================================');
      console.log('🔍 SYNC STEP:', stepKey);
      console.log('🔍 ========================================');
      
      // ✅ CORRECTION : Utiliser cloneManager au lieu d'apiService
      const token = cloneManager.getToken();
      const clone = cloneManager.getClone();
      
      console.log('🔍 Clone:', clone);
      console.log('🔍 Token présent:', !!token);
      console.log('🔍 API URL:', clone?.api_url);
      
      if (!token) {
        console.error('❌ ========================================');
        console.error('❌ PAS DE TOKEN !');
        console.error('❌ ========================================');
        console.error('❌ Clone data:', clone);
        console.error('❌ LocalStorage active_clone:', localStorage.getItem('active_clone'));
        
        alert('⚠️ Vous devez être connecté pour synchroniser.\n\nVeuillez vous déconnecter et vous reconnecter.');
        return false;
      }

      if (stepKey === 'pending_photos') {
        console.log('⏭️ Sync ignorée pour pending_photos (géré localement)');
        return true;
      }

      console.log('✅ Token trouvé, préparation upload photos puis sync...');

      let latestStepData = stepData;
      try {
        const fromStorage = await inspectionStorage.getStep(missionId, stepKey);
        if (fromStorage != null) {
          latestStepData = fromStorage;
        }
      } catch (_) {
        // garde stepData passé en paramètre
      }

      let preparedStepData = latestStepData;
      try {
        const prepared = await prepareStepForSync(missionId, stepKey, latestStepData);
        if (prepared?.skip) return true;
        preparedStepData = prepared.stepData;
      } catch (prepErr) {
        console.warn(
          '⚠️ prepareStepForSync échoué, sync sans upload photos:',
          prepErr?.message || prepErr,
        );
      }

      const mappedKey = stepKey === 'etape_8_securite' ? 'etape_7_securite' : stepKey;
      const payload = await attachPendingPhotosToSyncPayload(missionId, {
        [mappedKey]: preparedStepData,
      });
      const result = await apiService.syncMissionData(missionId, payload);

      if (result.success) {
        console.log(`✅ Sync réussie pour ${stepKey}`);
        await inspectionStorage.set(`mission_${missionId}_last_sync`, new Date().toISOString());
        if (result.data?.rapport?.id) {
          await saveRapportMetaFromApiRapport(
            inspectionStorage,
            missionId,
            result.data.rapport,
          );
        }
        return true;
      } else {
        console.warn(`⚠️ Sync échouée pour ${stepKey}:`, result.error);
        this.addToSyncQueue(missionId, stepKey, stepData);
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur sync:', error);
      this.addToSyncQueue(missionId, stepKey, stepData);
      return false;
    }
  }

  addToSyncQueue(missionId, stepKey, stepData) {
    const existingIndex = this.syncQueue.findIndex(
      item => item.missionId === missionId && item.stepKey === stepKey
    );

    if (existingIndex >= 0) {
      this.syncQueue[existingIndex] = { missionId, stepKey, stepData, timestamp: Date.now() };
    } else {
      this.syncQueue.push({ missionId, stepKey, stepData, timestamp: Date.now() });
    }

    this.saveQueueToStorage();
    console.log(`📋 Queue de sync: ${this.syncQueue.length} éléments`);
  }

  saveQueueToStorage() {
    try {
      localStorage.setItem('sync_queue', JSON.stringify(this.syncQueue));
    } catch (error) {
      console.error('Erreur sauvegarde queue:', error);
    }
  }

  loadQueueFromStorage() {
    try {
      const saved = localStorage.getItem('sync_queue');
      if (saved) {
        this.syncQueue = JSON.parse(saved);
        console.log(`📋 Queue chargée: ${this.syncQueue.length} éléments`);
      }
    } catch (error) {
      console.error('Erreur chargement queue:', error);
      this.syncQueue = [];
    }
  }

  async processSyncQueue() {
    if (this.isSyncing || this.syncQueue.length === 0) return;

    this.isSyncing = true;
    console.log(`🔄 Traitement de ${this.syncQueue.length} éléments en queue...`);

    const itemsToProcess = [...this.syncQueue];
    this.syncQueue = [];

    for (const item of itemsToProcess) {
      await this.syncStep(item.missionId, item.stepKey, item.stepData);
    }

    this.saveQueueToStorage();
    this.isSyncing = false;
    console.log('✅ Traitement queue terminé');
  }

  startNetworkListener() {
    this.loadQueueFromStorage();

    window.addEventListener('online', () => {
      console.log('📶 Retour en ligne - traitement queue...');
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      console.log('🔴 Passage hors ligne');
    });

    if (navigator.onLine && this.syncQueue.length > 0) {
      this.processSyncQueue();
    }
  }

  // ✅ CORRIGÉ - Sync complète de toutes les missions
  async syncAllMissions() {
    try {
      console.log('🔍 ========================================');
      console.log('🔍 SYNC ALL MISSIONS');
      console.log('🔍 ========================================');
      console.log('🔄 Début de la synchronisation de toutes les missions...');
      
      // Vérifier l'authentification AVANT de commencer
      const token = cloneManager.getToken();
      if (!token) {
        console.error('❌ Pas de token pour syncAllMissions');
        throw new Error('Non authentifié - impossible de synchroniser');
      }
      
      console.log('✅ Token présent, récupération des missions...');
      
      const missions = await inspectionStorage.getAllMissionsWithData();
      
      if (!missions || missions.length === 0) {
        console.log('ℹ️ Aucune mission à synchroniser');
        return { success: true, synced: 0, errors: 0, total: 0 };
      }

      console.log(`📋 ${missions.length} missions à synchroniser`);

      let syncedCount = 0;
      let errorCount = 0;

      for (const mission of missions) {
        try {
          const success = await this.syncFullMission(mission.id);
          if (success) {
            syncedCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error(`❌ Erreur sync mission ${mission.id}:`, error);
          errorCount++;
        }
      }

      console.log(`✅ Synchronisation terminée: ${syncedCount} missions synchronisées, ${errorCount} erreurs`);
      
      return {
        success: errorCount === 0,
        synced: syncedCount,
        errors: errorCount,
        total: missions.length
      };
    } catch (error) {
      console.error('❌ Erreur syncAllMissions:', error);
      throw error;
    }
  }

  async syncPendingPhotos(missionId) {
    if (!navigator.onLine) {
      return { success: false, error: 'Hors ligne' };
    }

    const token = cloneManager.getToken();
    if (!token) {
      return { success: false, error: 'Non authentifié' };
    }

    try {
      const payload = await attachPendingPhotosToSyncPayload(missionId, {});
      const result = await apiService.syncMissionData(missionId, payload);
      if (result.success) {
        await inspectionStorage.set(`mission_${missionId}_last_sync`, new Date().toISOString());
        if (result.data?.rapport?.id) {
          await saveRapportMetaFromApiRapport(
            inspectionStorage,
            missionId,
            result.data.rapport,
          );
        }
      }
      return result;
    } catch (error) {
      console.error('❌ Erreur syncPendingPhotos:', error);
      return { success: false, error: error.message };
    }
  }

  async syncFullMission(missionId) {
    try {
      const result = await syncService.uploadMission(missionId);

      if (result.success) {
        if (result.data?.rapport?.id) {
          await saveRapportMetaFromApiRapport(
            inspectionStorage,
            missionId,
            result.data.rapport,
          );
        }
        console.log(`✅ Sync complète mission ${missionId} terminée`);
        return true;
      }

      console.warn(`⚠️ Sync complète mission ${missionId} échouée:`, result.error);
      return false;
    } catch (error) {
      console.error('❌ Erreur sync complète:', error);
      return false;
    }
  }

  getSyncStatus() {
    return {
      queueLength: this.syncQueue.length,
      isSyncing: this.isSyncing,
      isOnline: navigator.onLine
    };
  }
}

const syncManager = new SyncManager();
syncManager.startNetworkListener();

export default syncManager;